import { translate as i18n } from "@/i18n";import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { GOOGLE_ANALYTICS_ENABLED } from "@/config/legal";
import { disableGoogleAnalytics, enableGoogleAnalytics, rememberedGoogleAnalyticsClientId, rememberAcquisitionSource, trackPageView, trackPublicEvent } from "@/lib/analytics";
import client from "@/lib/api";
import { Cookie } from "lucide-react";

export const CONSENT_STORAGE_KEY = "ef_consent_v2";
export const CONSENT_VERSION = "2026-08-07";
const CONSENT_SCOPE = "necessary-analytics-marketing";

const readConsent = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY) || "null");
    if (parsed?.version === CONSENT_VERSION && parsed?.scope === CONSENT_SCOPE) return parsed;
  } catch {}
  return null;
};

export const openCookiePreferences = () => window.dispatchEvent(new Event("ef:open-cookie-preferences"));

export default function CookieConsent() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [analyticsActive, setAnalyticsActive] = useState(false);

  useEffect(() => {
    const saved = readConsent();
    if (!saved) {
      disableGoogleAnalytics();
      setVisible(true);
      return;
    }
    setAnalytics(Boolean(saved.analytics));
    setMarketing(Boolean(saved.marketing));
    if (GOOGLE_ANALYTICS_ENABLED && saved.analytics) setAnalyticsActive(enableGoogleAnalytics());else
    disableGoogleAnalytics();
  }, []);

  useEffect(() => {
    const open = () => {
      const saved = readConsent();
      setAnalytics(Boolean(saved?.analytics));
      setMarketing(Boolean(saved?.marketing));
      setPrefsOpen(true);
    };
    window.addEventListener("ef:open-cookie-preferences", open);
    return () => window.removeEventListener("ef:open-cookie-preferences", open);
  }, []);

  useEffect(() => {
    if (analyticsActive) {
      rememberAcquisitionSource(location);
      trackPageView(location);
      if (["/diagnostico", "/en/diagnosis"].includes(location.pathname)) trackPublicEvent("diagnosis_entry");
    }
  }, [analyticsActive, location]);

  const persist = async (choices) => {
    const next = { analytics: Boolean(choices.analytics), marketing: Boolean(choices.marketing) };
    // A withdrawal must also make the server unable to attach later Stripe
    // renewals to this browser's analytics instance. The untrusted browser ID
    // can only opt out; the API never creates or enables measurement from it.
    const withdrawnClientId = !next.analytics && analytics
      ? rememberedGoogleAnalyticsClientId()
      : null;
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        scope: CONSENT_SCOPE,
        necessary: true,
        ...next,
        updatedAt: new Date().toISOString()
      }));
    } catch {}
    if (withdrawnClientId) {
      client.post("/analytics/revoke-client-id", {
        analytics_client_id: withdrawnClientId,
      }).catch(() => {});
    }
    setAnalytics(next.analytics);
    setMarketing(next.marketing);
    if (GOOGLE_ANALYTICS_ENABLED && next.analytics) {
      const enabled = enableGoogleAnalytics();
      rememberAcquisitionSource(location);
      setAnalyticsActive(enabled);
    } else
    {
      disableGoogleAnalytics();
      setAnalyticsActive(false);
    }
    setVisible(false);
    setPrefsOpen(false);
  };

  return (
    <>
      {visible &&
      <div data-testid="cookie-banner" className="fixed bottom-0 inset-x-0 z-[60] p-3 sm:p-4">
          <div className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-2xl soft-shadow p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-9 h-9 rounded-xl bg-[#2A9D8F]/10 flex items-center justify-center shrink-0"><Cookie className="w-5 h-5 text-[#2A9D8F]" /></div>
              <p className="text-sm text-slate-600">{i18n("ui.components_cookieconsent.usamos_almacenamiento_necesario_para_la_au")}<Link to="/legal/cookies" className="text-[#2A9D8F] underline">{i18n("ui.components_cookieconsent.politica_de_cookies")}</Link>.</p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => setPrefsOpen(true)} data-testid="cookie-prefs" className="rounded-xl">{i18n("ui.components_cookieconsent.configurar")}</Button>
              <Button variant="outline" size="sm" onClick={() => persist({ analytics: false, marketing: false })} data-testid="cookie-reject-all" className="rounded-xl border-[#0F4C5C] text-[#0F4C5C]">{i18n("ui.components_cookieconsent.rechazar_todas")}</Button>
              <Button variant="outline" size="sm" onClick={() => persist({ analytics: true, marketing: true })} data-testid="cookie-accept-all" className="rounded-xl border-[#0F4C5C] text-[#0F4C5C]">{i18n("ui.components_cookieconsent.aceptar_todas")}</Button>
            </div>
          </div>
        </div>
      }

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle>{i18n("ui.components_cookieconsent.preferencias_de_cookies")}</DialogTitle>
            <DialogDescription>{i18n("ui.components_cookieconsent.usamos_almacenamiento_necesario_para_la_au")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <PreferenceRow title={i18n("ui.components_cookieconsent.necesarias")} description={i18n("ui.components_cookieconsent.necessaryDescription")} disabled checked />
            <PreferenceRow title={i18n("ui.components_cookieconsent.analiticas")} description={i18n("ui.components_cookieconsent.analyticsDescription")} checked={analytics} onChange={setAnalytics} testid="pref-analytics" />
            <PreferenceRow title={i18n("ui.components_cookieconsent.marketing")} description={i18n("ui.components_cookieconsent.marketingDescription")} checked={marketing} onChange={setMarketing} testid="pref-marketing" />
          </div>
          <DialogFooter className="gap-2 sm:space-x-0">
            <Button variant="outline" onClick={() => persist({ analytics: false, marketing: false })} data-testid="cookie-save-reject" className="rounded-xl border-[#0F4C5C] text-[#0F4C5C]">{i18n("ui.components_cookieconsent.rechazar_todas")}</Button>
            <Button variant="outline" onClick={() => persist({ analytics, marketing })} data-testid="cookie-save" className="rounded-xl border-[#0F4C5C] text-[#0F4C5C]">{i18n("ui.components_cookieconsent.guardar_seleccion")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>);

}

function PreferenceRow({ title, description, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl p-3">
      <div><div className="text-sm font-medium text-slate-800">{title}</div><div className="text-xs text-slate-400">{description}</div></div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
    </div>);

}
